import { DRAWING_HANDLE_HIT_PX, DRAWING_LINE_HIT_PX, DRAWING_TOUCH_HIT_PX } from "./drawingConstants";

const distanceToSegment = ({ x, y }, [x0, y0, x1, y1]) => {
  const deltaX = x1 - x0;
  const deltaY = y1 - y0;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  const ratio = lengthSquared === 0 ? 0 : ((x - x0) * deltaX + (y - y0) * deltaY) / lengthSquared;
  const along = Math.min(1, Math.max(0, ratio));

  return Math.hypot(x - (x0 + deltaX * along), y - (y0 + deltaY * along));
};

const isInsideFill = ({ x, y }, [x0, top0, x1, top1, bottom0, bottom1]) => {
  if (x < Math.min(x0, x1) || x > Math.max(x0, x1)) return false;

  const ratio = x0 === x1 ? 0 : (x - x0) / (x1 - x0);
  const top = top0 + (top1 - top0) * ratio;
  const bottom = bottom0 + (bottom1 - bottom0) * ratio;

  return y >= Math.min(top, bottom) && y <= Math.max(top, bottom);
};

const hitShape = (shape, point, isTouch, isSelected) => {
  // A finger only takes the handles of the selected drawing: its drags over the others pan the chart
  if (isSelected || !isTouch) {
    const reach = isTouch ? DRAWING_TOUCH_HIT_PX : DRAWING_HANDLE_HIT_PX;
    const pointIndex = shape.handles.findIndex(({ x, y }) => Math.hypot(point.x - x, point.y - y) <= reach);

    if (pointIndex >= 0) return { id: shape.item.id, pointIndex };
  }

  const reach = isTouch ? DRAWING_TOUCH_HIT_PX : DRAWING_LINE_HIT_PX;
  const isOnLine = shape.strokes.some(({ segments, width }) =>
    segments.some((segment) => distanceToSegment(point, segment) <= reach + width / 2),
  );
  // Inside a filled drawing only while selected, otherwise a big one would block the pan under it
  const isOnFill = isSelected && shape.fills.some(({ traps }) => traps.some((trap) => isInsideFill(point, trap)));

  return isOnLine || isOnFill ? { id: shape.item.id, pointIndex: null } : null;
};

/**
 * The drawing under the pointer: the selected one first, then from the top
 * (the last drawn) down.
 *
 * @param {object[]} shapes - from resolveShape, in the drawing order
 * @param {{ x: number, y: number }} point - CSS px of the pane
 * @param {{ isTouch: boolean, selectedId: string|null }} params
 * @returns {{ id: string, pointIndex: number|null } | null} pointIndex: the handle under the pointer
 */
export const hitTestDrawings = (shapes, point, { isTouch, selectedId }) => {
  const selected = shapes.find((shape) => shape.item.id === selectedId);
  const hitSelected = selected ? hitShape(selected, point, isTouch, true) : null;

  if (hitSelected) return hitSelected;

  for (let index = shapes.length - 1; index >= 0; index -= 1) {
    const hit = shapes[index] === selected ? null : hitShape(shapes[index], point, isTouch, false);

    if (hit) return hit;
  }

  return null;
};
